/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { TrendingUp, RefreshCw } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from 'recharts';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getRevenueAnalytics } from '@/lib/invoicing';
import { THEME_CONFIG } from '@/config/themeConfig';

const euro = (n) => `€ ${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const RevenueCharts = () => {
  const [period, setPeriod] = useState('monthly');
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (p = period, y = year) => {
    setLoading(true);
    try {
      const res = await getRevenueAnalytics({ period: p, year: y });
      setData(res);
    } catch (e) {
      toast.error('Impossibile caricare analytics ricavi');
    } finally {
      setLoading(false);
    }
  }, [period, year]);

  useEffect(() => { load(period, year); }, [period, year, load]);

  const chartData = useMemo(() => {
    return (data?.buckets || []).map((b) => ({
      name: b.label,
      totale: b.total,
      imponibile: Number((b.total - b.vat).toFixed(2)),
      iva: b.vat,
      fatture: b.count,
    }));
  }, [data]);

  const summary = data?.summary || { total: 0, vat: 0, count: 0, avg_per_invoice: 0, cancelled: 0 };
  const primary = `var(--wl-primary, ${THEME_CONFIG.colors.primary})`;

  return (
    <Card data-testid="revenue-charts">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" /> Analisi ricavi
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Aggregato delle fatture <b>emesse</b> (le annullate sono escluse).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="h-8 rounded-md border bg-background px-2 text-xs"
            data-testid="revenue-year-select"
          >
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <Button variant="outline" size="icon" onClick={() => load()} aria-label="Aggiorna">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="p-3 rounded-lg border bg-primary/5">
            <div className="text-xs text-muted-foreground">Totale anno</div>
            <div className="text-lg font-bold text-primary" data-testid="revenue-total">{euro(summary.total)}</div>
          </div>
          <div className="p-3 rounded-lg border">
            <div className="text-xs text-muted-foreground">IVA cumulata</div>
            <div className="text-lg font-semibold">{euro(summary.vat)}</div>
          </div>
          <div className="p-3 rounded-lg border">
            <div className="text-xs text-muted-foreground">Fatture emesse</div>
            <div className="text-lg font-semibold">{summary.count}</div>
          </div>
          <div className="p-3 rounded-lg border">
            <div className="text-xs text-muted-foreground">Media / fattura</div>
            <div className="text-lg font-semibold">{euro(summary.avg_per_invoice)}</div>
          </div>
        </div>

        <Tabs value={period} onValueChange={setPeriod} data-testid="revenue-tabs">
          <TabsList>
            <TabsTrigger value="monthly" data-testid="revenue-tab-monthly">Mensile</TabsTrigger>
            <TabsTrigger value="quarterly" data-testid="revenue-tab-quarterly">Trimestrale</TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              {period === 'monthly' ? (
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis fontSize={12} tickFormatter={(v) => `€ ${v}`} />
                  <Tooltip formatter={(v) => euro(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="totale" stroke={primary} strokeWidth={2.5} name="Totale (con IVA)" dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="imponibile" stroke="#64748B" strokeWidth={1.5} strokeDasharray="5 5" name="Imponibile" dot={{ r: 2 }} />
                </LineChart>
              ) : (
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis fontSize={12} tickFormatter={(v) => `€ ${v}`} />
                  <Tooltip formatter={(v) => euro(v)} />
                  <Legend />
                  <Bar dataKey="imponibile" fill="#94A3B8" name="Imponibile" />
                  <Bar dataKey="iva" fill={primary} name="IVA" />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        )}

        {summary.cancelled > 0 && (
          <Badge variant="outline" className="text-xs">
            {summary.cancelled} fattura(e) annullata(e) esclusa(e)
          </Badge>
        )}
      </CardContent>
    </Card>
  );
};

export default RevenueCharts;
